import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { splitAddress } from "@/lib/clientAddress";
import { countryCodeOf } from "@/components/CityInput";

const view = (cl = {}) => { const s = splitAddress(cl); return { ...cl, ...s, country: countryCodeOf(s.country, "") || s.country }; };

const FIELDS = [["firstname", "Prénom"], ["name", "Nom"], ["company", "Société"], ["siret", "SIRET"], ["email", "E-mail"], ["phone", "Téléphone"],
  ["address", "Adresse"], ["postal", "Code postal"], ["city", "Ville"], ["country", "Pays (code, ex. FR)"]];

export const AdvComments = ({ list, n }) => (
  <div className="space-y-1.5" data-testid={`adv-comments-${n}`}>
    {(list || []).map((c) => (
      <div key={c.id} className="text-xs rounded-md bg-amber-50 border border-amber-200 px-2 py-1" data-testid={`adv-comment-${c.id}`}>
        <div className="whitespace-pre-wrap">{c.text}</div>
        <div className="text-[10px] text-muted-foreground">{c.by} · {new Date(c.at).toLocaleString("fr-FR")}</div>
      </div>
    ))}
  </div>
);

// Seller: fix client details of a quote / signed order + leave notes for the ADV team
export const OrderClientDialog = ({ quote, onClose, onSaved, clientEditable = true }) => {
  const [c, setC] = useState({});
  const [comments, setComments] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!quote) return;
    setC(view(quote.client)); setComments(quote.adv_comments || []); setNote("");
  }, [quote]);
  if (!quote) return null;
  const set = (k, v) => setC((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const patch = {};
      if (note.trim()) {
        const { data } = await api.post(`/quotes/${quote.id}/adv-comments`, { text: note.trim() });
        patch.adv_comments = data.adv_comments;
        setComments(data.adv_comments); setNote("");
      }
      const orig = view(quote.client);
      if (clientEditable && FIELDS.some(([k]) => (c[k] || "").trim() !== (orig[k] || "").trim())) {
        const body = Object.fromEntries(FIELDS.map(([k]) => [k, c[k] || ""]));
        const { data } = await api.put(`/quotes/${quote.id}/order-client`, { ...body, client_type: c.company ? "societe" : (c.client_type || "") });
        patch.client = data.client;
      }
      toast.success("Enregistré");
      onSaved(quote.id, patch);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Enregistrement impossible");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto" data-testid="order-client-dialog">
        <DialogHeader>
          <DialogTitle>{clientEditable ? "Client et précisions ADV" : "Précisions ADV"} — {quote.quote_number}</DialogTitle>
          <DialogDescription>{quote.signature ? "Commande signée : les modifications ne demandent pas de nouvelle signature et sont notées dans l'historique." : "Les modifications sont enregistrées sur le devis."}</DialogDescription>
        </DialogHeader>
        {clientEditable && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {FIELDS.map(([k, l]) => (
              <div key={k} className={k === "address" ? "sm:col-span-2" : ""}><Label className="text-xs">{l}{(k === "name" || k === "firstname") && <span className="text-destructive"> *</span>}</Label>
                <Input value={c[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1 h-9" data-testid={`oc-${k}`} /></div>
            ))}
          </div>
        )}
        <div className="space-y-2 pt-2 border-t border-border">
          <Label className="text-sm font-semibold">Commentaires et précisions pour l'ADV</Label>
          <AdvComments list={comments} n={quote.quote_number} />
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={3} placeholder="Ex. : livraison à prévoir le samedi, client joignable après 18 h…" data-testid="oc-adv-note" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={busy} data-testid="oc-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
